import { supabase } from '@/lib/supabase';
import type {
  Notification,
  NotificationFilter,
  NotificationCursor,
} from '@/types';
import { ACTIVITY_WINDOW_DAYS } from '@/constants/activity';

const PAGE_SIZE = 20;

/** Oldest `created_at` the feed and its unread count still include. */
function activityWindowStart(): string {
  return new Date(
    Date.now() - ACTIVITY_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  ).toISOString();
}

/**
 * Load a page of notifications for the ACTIVE household.
 * Supports keyset pagination and All/Unread filtering.
 *
 * `householdId` is required, and deliberately so. RLS on `notifications`
 * scopes to `recipient_user_id`, which is the *user* — and a user spans
 * every household they belong to. Leaving the binding to RLS therefore
 * merges the feeds of all of them, which is exactly what YAR-358 reported
 * (3 production users, 138 undismissed rows visible outside the household
 * being viewed). Same class of bug as YAR-310 on Today/Schedule/History.
 *
 * Required rather than optional so adding a call site is a compile error
 * rather than a silent cross-household leak.
 */
export async function listNotifications(
  householdId: string,
  filter: NotificationFilter = 'all',
  cursor: NotificationCursor = null,
): Promise<{ data: Notification[]; nextCursor: NotificationCursor }> {
  let query = supabase
    .from('notifications')
    .select('*')
    .eq('household_id', householdId)
    .is('dismissed_at', null)
    .gte('created_at', activityWindowStart())
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(PAGE_SIZE);

  if (filter === 'unread') {
    query = query.is('read_at', null);
  }

  if (cursor) {
    query = query.or(
      `created_at.lt.${cursor.created_at},` +
        `and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
    );
  }

  const { data, error } = await query;
  if (error) throw error;

  const items = (data ?? []) as Notification[];

  const nextCursor: NotificationCursor =
    items.length === PAGE_SIZE
      ? {
          created_at: items[items.length - 1].created_at,
          id: items[items.length - 1].id,
        }
      : null;

  return { data: items, nextCursor };
}

/**
 * Unread count for the bell badge. Capped at 100 — an exact count over
 * a potentially unbounded table is expensive and the UI only shows
 * "99+" once the value exceeds 99, so we bound the fetch and return
 * `data.length`.
 *
 * Household-bound for the same reason as `listNotifications`: without it the
 * badge counts other households' unread rows, so it disagrees with the list
 * the user is actually shown.
 */
const UNREAD_COUNT_CAP = 100;

export async function getUnreadCount(householdId: string): Promise<number> {
  const { data, error } = await supabase
    .from('notifications')
    .select('id')
    .eq('household_id', householdId)
    .is('read_at', null)
    .is('dismissed_at', null)
    .gte('created_at', activityWindowStart())
    .limit(UNREAD_COUNT_CAP);

  if (error) throw error;
  return data?.length ?? 0;
}

/** Mark a single notification as read (tap to navigate). */
export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', notificationId)
    .is('read_at', null);

  if (error) throw error;
}

/**
 * Mark all unread notifications as read (overflow menu action).
 *
 * Bound to the active household so "Mark all read" clears what the user is
 * looking at. Unbound, it silently marked another household's feed read from
 * a screen that never displayed it.
 */
export async function markAllAsRead(householdId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('household_id', householdId)
    .is('read_at', null);

  if (error) throw error;
}

/** Dismiss a notification (soft delete — hidden from list, purged by cron). */
export async function dismissNotification(
  notificationId: string,
): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ dismissed_at: new Date().toISOString() })
    .eq('id', notificationId);

  if (error) throw error;
}
